<template>
  <div
    class="wishlist-button"
    :class="[
      { 'is-added' : isAdded },
      { 'is-loading' : state === 'loading' },
    ]"
  >
    <button
      class="wishlist-button__button swym-button swym-icon swym-heart swym-loaded"
      type="button"
      :aria-label="buttonLabel"
      @click.stop.prevent="handleWishlistClick"
    >
      <icon-wishlist />
    </button>
  </div>
</template>

<script>

/**
 * Component: Wishlist Button
 * ------------------------------------------------------------------------------
 * - Reusable Wishlist Button component
 * - Currently uses OOTB Swym Wishlist methods
 * - This is because Swym JS SDK cannot trigger the 'Select Wishlist' modal
 * - During the Wishlist Page redesign, MV may move to a single Wishlist,
 *   in which case the commented out buttons above can be swapped out.
 * - When using the JS SDK, the Wishlist will be synced with Pinia
 * - Not really possible with the OOTB popup without Mutation Observer.
 * @param {Object} currentVariant - Current variant (formatted)
 * @param {Object} product - Formatted Shopify product.
 */
import { mapActions, mapState } from 'pinia'

import { useWishlistStore } from '~stores/wishlist'

import IconWishlist from '~icons/wishlist/heart.svg'

export default {
  name: 'WishlistButton',

  components: {
    IconWishlist,
  },

  props: {
    currentVariant: {
      type: [Boolean, Object],
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      isAdded: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useWishlistStore, {
      ready: 'wishlistReady',
      wishlistItems: 'wishlistData',
    }),

    /**
     * Calculate the currently selected variant.
     * @returns {Number|Boolean}
     */
    activeVariantId() {
      if (this.currentVariant?.id) {
        return this.currentVariant.id
      }

      if (this.product?.variants?.length) {
        return this.product.variants[0].id
      }

      return false
    },

    /**
     * Compute a Swym-compatible Product URL.
     * @returns {String}
     */
    productUrl() {
      return `${location.protocol}${this.product.url}`
    },

    /**
     * Calculate the accessible button label.
     * @returns {String}
     */
    buttonLabel() {
      const {
        addToWishlist,
        removeFromWishlist,
      } = mint.strings.wishlist.button

      return this.isAdded ? removeFromWishlist : addToWishlist
    },
  },

  watch: {

    /**
     * The Wishlist has been updated.
     */
    wishlistItems: {
      handler() {
        this.setAddedState()
      },
      deep: true,
    },

    /**
     * Watch the 'ready' state of the Wishlist SDK.
     */
    ready: {
      handler() {
        this.state = this.ready ? 'ready' : 'loading'

        if (!this.ready) {
          return
        }

        this.setAddedState()
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Map Wishlist methods.
     */
    ...mapActions(useWishlistStore, [
      'addToWishlist',
      'removeFromWishlist',
    ]),

    /**
     * Define if the product is in the Wishlist.
     * @returns {Boolean}
     */
    setAddedState() {
      this.isAdded = false

      if (!this.ready) {
        return false
      }
      const isAdded = Array.isArray(this.wishlistItems)
        ? this.wishlistItems?.find((product) => {
          return Number(this.product.id) === Number(product.empi)
        })
        : false

      this.isAdded = Boolean(isAdded)
    },

    /**
     * Events for when the user interacts with the icon.
     */
    async handleWishlistClick() {
      try {
        const currentVariant = this.currentVariant || this.product.variants[0]

        if (this.isAdded) {
          await this.removeFromWishlist(this.product, currentVariant)

        } else {
          await this.addToWishlist(this.product, currentVariant)
        }

      } catch (error) {
        mint.Log('error', 'Unable to toggle Wishlist', error)

      }
    },
  },
}
</script>

<style src="./wishlist-button.scss" lang="scss"></style>